import { useAuthStore } from '../stores/auth.store';

// Single source of truth: URL base dell'API con prefix versione.
// Esportato perché alcuni file (download raw, signing page pubblica) hanno
// bisogno di fare fetch fuori dal wrapper `apiFetch` (es. per blob download).
export const API_BASE_URL: string = (import.meta.env.VITE_API_URL ?? 'http://localhost:4000') + '/api/v1';
const BASE_URL = API_BASE_URL;

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) {
    super(message);
  }
}

type FetchOpts = RequestInit & { auth?: boolean };

let refreshInflight: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshInflight) return refreshInflight;
  refreshInflight = (async () => {
    try {
      const res = await fetch(`${BASE_URL}/auth/refresh`, { method: 'POST', credentials: 'include' });
      if (!res.ok) return null;
      const data = await res.json();
      useAuthStore.getState().setAuth(data.user, data.accessToken);
      return data.accessToken as string;
    } catch {
      return null;
    } finally {
      refreshInflight = null;
    }
  })();
  return refreshInflight;
}

export async function apiFetch<T = unknown>(path: string, opts: FetchOpts = {}): Promise<T> {
  const { auth = true, headers, ...rest } = opts;
  const token = useAuthStore.getState().accessToken;

  const doFetch = async (t: string | null) => {
    const finalHeaders: Record<string, string> = {
      ...(headers as Record<string, string> | undefined),
    };
    // Set Content-Type ONLY if there's a body (Fastify complains on empty JSON body)
    if (rest.body !== undefined && rest.body !== null && !finalHeaders['Content-Type']) {
      finalHeaders['Content-Type'] = 'application/json';
    }
    if (auth && t) finalHeaders['Authorization'] = `Bearer ${t}`;
    return fetch(`${BASE_URL}${path}`, { ...rest, credentials: 'include', headers: finalHeaders });
  };

  let res = await doFetch(token);

  if (res.status === 401 && auth) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      res = await doFetch(newToken);
    } else {
      useAuthStore.getState().clear();
      throw new ApiError(401, 'UNAUTHORIZED', 'Session expired');
    }
  }

  if (!res.ok) {
    let payload: any = null;
    try { payload = await res.json(); } catch {}
    // Il server risponde in due forme: { error: { code, message } } quando passa
    // dal nostro error handler, e { statusCode, code, error, message } quando la
    // risposta la confeziona Fastify (lì `error` è la stringa "Bad Request").
    // Le frasi scritte per chi legge stanno nella seconda: senza questo fallback
    // arrivavano nei toast come "Bad Request", cioè come un errore tecnico.
    const nested = typeof payload?.error === 'object' ? payload.error : null;
    throw new ApiError(
      res.status,
      nested?.code ?? payload?.code ?? 'ERROR',
      nested?.message ?? payload?.message ?? res.statusText,
      nested?.details ?? payload?.details,
    );
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  get: <T = unknown>(path: string, opts?: FetchOpts) => apiFetch<T>(path, { ...opts, method: 'GET' }),
  post: <T = unknown>(path: string, body?: unknown, opts?: FetchOpts) =>
    apiFetch<T>(path, { ...opts, method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch: <T = unknown>(path: string, body?: unknown, opts?: FetchOpts) =>
    apiFetch<T>(path, { ...opts, method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  put: <T = unknown>(path: string, body?: unknown, opts?: FetchOpts) =>
    apiFetch<T>(path, { ...opts, method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  delete: <T = unknown>(path: string, opts?: FetchOpts) => apiFetch<T>(path, { ...opts, method: 'DELETE' }),
};
